import React, { useState } from 'react';

import Form, { Field } from 'mobx-react-form';
import ObjectID from 'bson-objectid';
import { observer } from 'mobx-react-lite';
import { cloneDeep, get, isNumber } from 'lodash';

import { Alert, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';

import AdjustmentsAndLossesToolbar from './AdjustmentsAndLossesToolbar';

import { useRootStore } from 'stores/RootStore';
import { SECTIONS, showSection } from 'components/underwriting/subject-property/constants';
import AdjustmentsAndLossesTableView from './AdjustmentsAndLossesTableView';
import { Property } from 'types/underwriting/property';
import { ValueConclusion } from 'types/underwriting/underwriting';
import RentLossModal from '../modals/RentLossModal';
import { CommercialRentRollUnit, RentRollUnit, ResidentialRentRollUnit } from 'types/underwriting/income-approach';
import { calculateMonthlyRentLoss } from '../calculations';
import { ConclusionType, RentLossType } from '../types';
import { getRentLossPath } from '../helpers';

type AdjustmentsAndLossesTableProps = {
  form: Form;
};

const AdjustmentsAndLossesTable = observer(({ form }: AdjustmentsAndLossesTableProps) => {
  const { underwritingPageStore } = useRootStore();
  const property = get(underwritingPageStore.underwritingData, 'property', {}) as Property;
  const propertyType = get(property, 'propertyType', '');
  const valueConclusions = get(
    underwritingPageStore.underwritingData,
    'settings.valueConclusions',
    []
  ) as ValueConclusion[];

  const commercialRentRoll = get(
    underwritingPageStore.underwritingData,
    'incomeApproach.commercial.rentRoll',
    []
  ) as CommercialRentRollUnit[];
  const residentialRentRoll = get(
    underwritingPageStore.underwritingData,
    'incomeApproach.residential.rentRoll',
    []
  ) as ResidentialRentRollUnit[];

  const [rentLossModalState, setRentLossModalState] = useState<{
    open: boolean;
    type: 'residential' | 'commercial' | null;
    isEditMode: boolean;
    index: number | null;
    conclusionType: 'asStabilized' | 'asComplete' | null;
  }>({
    open: false,
    type: null,
    isEditMode: false,
    index: null,
    conclusionType: null,
  });

  const valueAsIs = valueConclusions.includes('asIs');
  const valueAsStabilized = valueConclusions.includes('asStabilized');
  const valueAsComplete = valueConclusions.includes('asComplete');

  const hasResidentialUnits =
    ['mixed-use', 'multifamily'].includes(propertyType) &&
    isNumber(property?.residentialUnits) &&
    property!.residentialUnits > 0;

  const hasCommercialUnits =
    ['mixed-use', 'commercial'].includes(propertyType) &&
    isNumber(property?.commercialUnits) &&
    property!.commercialUnits > 0;

  const addRentLossItem = (rentLossDataPath: string, units: RentRollUnit[], vcLossPercentage: number) => {
    const monthlyLoss = calculateMonthlyRentLoss(units, vcLossPercentage);
    const unitIds = units.map((unit) => unit.id);

    try {
      form.$(rentLossDataPath).add([
        {
          id: new ObjectID().toHexString(),
          monthlyLoss,
          amount: 0,
          months: 0,
          units: unitIds,
        },
      ]);
    } catch (error) {
      console.error('Error adding rent loss item', error);
    }
  };

  const removeRentLossItem = (index: number, rentLossType: RentLossType, conclusionType: ConclusionType) => {
    try {
      const targetField = form.$(getRentLossPath(rentLossType, conclusionType));
      const newFieldValue = (targetField.value || []).filter((_: any, i: number) => i !== index);

      targetField.set(newFieldValue);
      targetField.del(newFieldValue.length);
    } catch (error) {
      console.error('Error removing rent loss', error);
    }
  };

  const updateRentLossItem = (
    index: number,
    rentLossType: RentLossType,
    conclusionType: ConclusionType,
    units: RentRollUnit[],
    vcLossPercentage: number
  ) => {
    if (!units.length) {
      removeRentLossItem(index, rentLossType, conclusionType);
      return;
    }

    try {
      const targetRootField: Field = form.$(getRentLossPath(rentLossType, conclusionType));
      const rentLosses = cloneDeep(targetRootField.value);
      const targetRentLoss = rentLosses[index];
      if (!targetRentLoss) {
        return;
      }

      const unitIds = units.map((unit) => unit.id);
      const monthlyLoss = calculateMonthlyRentLoss(units, vcLossPercentage);
      const amount = monthlyLoss * (targetRentLoss.months || 0);

      const updatedItem = {
        ...targetRentLoss,
        amount,
        monthlyLoss,
        units: unitIds,
      };

      rentLosses[index] = updatedItem;

      /*
       * Workaround: The MobX form, when using the standard `.set` method, does not update
       * the `units` field value of RentLoss objects. Other values are updated correctly.
       * Directly updating the value using `form.$(`${getRentLossPath(rentLossType, conclusionType)}.${index}.units`)`
       * also has no effect.
       *
       * Using `.add` sets all values correctly, including the `units` field.
       */
      rentLosses.forEach((_: any, index: number) => {
        targetRootField.del(index.toString());
      });

      rentLosses.forEach((rentLoss: any) => {
        targetRootField.add([rentLoss]);
      });
    } catch (error) {
      console.error('Error updating rent loss item', error);
    }
  };

  const openRentLossModal = (type: RentLossType, isEditMode: boolean = false) => {
    setRentLossModalState({ open: true, type, isEditMode, index: null, conclusionType: null });
  };

  const closeRentLossModal = () => {
    setRentLossModalState({ open: false, type: null, isEditMode: false, index: null, conclusionType: null });
  };

  const onEditRentLoss = (index: number, rentLossType: RentLossType, conclusionType: ConclusionType) => {
    setRentLossModalState({ open: true, type: rentLossType, isEditMode: true, index, conclusionType });
  };

  return (
    <>
      <Box>
        <Stack spacing={2}>
          <Typography variant="subtitle1">
            <strong>Cap Rate Conclusion Summary Table</strong>
          </Typography>
          {showSection(SECTIONS.HAS_RESIDENTIAL, propertyType) && !hasResidentialUnits && !valueAsIs && (
            <Alert severity="warning">There are no residential units to deduct rent loss from.</Alert>
          )}
          {showSection(SECTIONS.HAS_COMMERCIAL, propertyType) && !hasCommercialUnits && !valueAsIs && (
            <Alert severity="warning">There are no commercial units to deduct rent loss from.</Alert>
          )}

          <AdjustmentsAndLossesToolbar
            form={form}
            hasResidentialUnits={hasResidentialUnits}
            hasCommercialUnits={hasCommercialUnits}
            valueConclusions={valueConclusions}
            valueAsComplete={valueAsComplete}
            valueAsStabilized={valueAsStabilized}
            openRentLossModal={openRentLossModal}
          />

          <AdjustmentsAndLossesTableView
            form={form}
            valueAsComplete={valueAsComplete}
            valueAsStabilized={valueAsStabilized}
            onEditRentLoss={onEditRentLoss}
            onRemoveRentLoss={removeRentLossItem}
          />
        </Stack>
      </Box>

      {rentLossModalState.open && rentLossModalState.type && (
        <RentLossModal
          form={form}
          isEditMode={rentLossModalState.isEditMode}
          index={rentLossModalState.index}
          type={rentLossModalState.type}
          valueAsComplete={valueAsComplete}
          valueAsStabilized={valueAsStabilized}
          VCLossPercentage={get(
            underwritingPageStore.underwritingData,
            `incomeApproach.${rentLossModalState.type}.vcLossPercentage`,
            0
          )}
          units={rentLossModalState.type === 'residential' ? residentialRentRoll : commercialRentRoll}
          handleClosed={closeRentLossModal}
          onAddRentLoss={addRentLossItem}
          onEditRentLoss={updateRentLossItem}
        />
      )}
    </>
  );
});

export default AdjustmentsAndLossesTable;
